/**
 * Billing tier definitions — single source of truth.
 *
 * Imported by:
 *   - src/routes/pricing.tsx              (renders the cards)
 *   - src/server/stripe.server.ts         (resolves tier → Stripe price id)
 *   - src/server/billing.server.ts        (resolves Stripe price id → tier)
 *   - src/routes/settings.tsx              (shows the user's current tier)
 *
 * Editing this file is the only place to change pricing copy + structure.
 * Stripe price ids live in env vars (STRIPE_PRICE_INDIE / PRO / TEAM) so
 * dashboards / test/live keys can switch without a code change.
 */

export type BillingTierId = "free" | "indie" | "pro" | "team";

export interface BillingTier {
  id: BillingTierId;
  name: string;
  /** Headline price (with currency symbol). Free tier shows "€0". */
  price: string;
  /** "/ mo", "/ year", "forever". */
  period: string;
  /** Short pitch, one sentence. */
  blurb: string;
  /** Monthly agent-run quota (null = unlimited / custom). */
  agentRunsPerMonth: number | null;
  /** Bullet list shown on the pricing card. Keep ≤ 5 items. */
  features: string[];
  /** Pricing card CTA label. */
  cta: string;
  /**
   * Env-var name that holds the Stripe price id for this tier.
   * Free has no Stripe price (signup is enough).
   */
  stripePriceEnvKey: string | null;
  /** Highlight as "Most popular" on the pricing page. */
  featured?: boolean;
}

export const BILLING_TIERS: Record<BillingTierId, BillingTier> = {
  free: {
    id: "free",
    name: "Free",
    price: "€0",
    period: "forever",
    blurb: "For tinkerers exploring the autonomous loop.",
    agentRunsPerMonth: 30,
    features: [
      "30 agent-runs / month",
      "1 active project",
      "Up to 4 agents (Haiku tier)",
      "Public projects only",
      "Community support",
    ],
    cta: "Start free",
    stripePriceEnvKey: null,
  },
  indie: {
    id: "indie",
    name: "Indie",
    price: "€19",
    period: "/ mo",
    blurb: "For solo builders shipping side projects.",
    agentRunsPerMonth: 300,
    features: [
      "300 agent-runs / month",
      "3 active projects",
      "Up to 6 agents (mixed models)",
      "GitHub PRs",
      "Email support",
    ],
    cta: "Go Indie",
    stripePriceEnvKey: "STRIPE_PRICE_INDIE",
  },
  pro: {
    id: "pro",
    name: "Pro",
    price: "€49",
    period: "/ mo",
    blurb: "For makers shipping their main product.",
    agentRunsPerMonth: 1200,
    features: [
      "1,200 agent-runs / month",
      "Unlimited projects / agents",
      "Premium models (Sonnet)",
      "Priority queue + pause/resume",
      "Email support + SLA",
    ],
    cta: "Start Pro",
    stripePriceEnvKey: "STRIPE_PRICE_PRO",
    featured: true,
  },
  team: {
    id: "team",
    name: "Team",
    price: "€149",
    period: "/ mo",
    blurb: "For 2–5 person crews shipping production work together.",
    agentRunsPerMonth: 4000,
    features: [
      "4,000 agent-runs / month",
      "3 seats included (€39 / addl.)",
      "Shared workspace",
      "SSO + audit log",
      "Slack support channel",
    ],
    cta: "Start Team",
    stripePriceEnvKey: "STRIPE_PRICE_TEAM",
  },
};

/** Ordered list for rendering. */
export const BILLING_TIER_ORDER: BillingTierId[] = ["free", "indie", "pro", "team"];

/** Tiers that go through Stripe Checkout (everything except Free). */
export const PAID_TIERS: BillingTierId[] = BILLING_TIER_ORDER.filter(
  (t) => BILLING_TIERS[t].stripePriceEnvKey !== null,
);

/**
 * Type guard for tier ids that may arrive from untrusted input
 * (e.g. POST body on /api/billing/checkout).
 */
export function isPaidTier(value: unknown): value is BillingTierId {
  return typeof value === "string" && (PAID_TIERS as string[]).includes(value);
}

/** Subscription row mirrored from Stripe into Supabase. */
export type SubscriptionStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "unpaid"
  | "paused";

export interface SubscriptionRow {
  user_id: string;
  tier: BillingTierId;
  status: SubscriptionStatus;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_price_id: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  metered_runs_this_period: number;
  created_at: string;
  updated_at: string;
}
